/**
 * Smart Color Palette utilities for uploaded SVGs.
 *
 * Goal: hide the dozens of raw colors / gradient stops behind a Canva-style
 * 3-slot palette (Primary / Secondary / Accent).  Editing one slot intelligently
 * remaps every other color in its family so that gradients, shadows, and
 * highlights all shift together while keeping their relative brightness and
 * contrast.
 *
 * Pure data utilities — no React, no DOM mutation.  Consumers feed in the raw
 * color list (from extractSvgColors) and receive a palette descriptor plus a
 * remap function.  Rendering is unchanged: the panel routes its edits through
 * the existing colorMap, which the SVG render pipeline already honors.
 */

import { extractSvgColors } from '../components/Editor/svgUtils';

// ── Color parsing & color-space conversions ─────────────────────────────────

// Subset of CSS named colors common in SVG exports.  Anything missing falls
// back to "uncluster-able" and stays out of the palette but is preserved
// verbatim in the SVG.
const NAMED_COLORS = {
  black: '#000000', white: '#ffffff', red: '#ff0000', lime: '#00ff00',
  green: '#008000', blue: '#0000ff', yellow: '#ffff00', cyan: '#00ffff',
  aqua: '#00ffff', magenta: '#ff00ff', fuchsia: '#ff00ff', silver: '#c0c0c0',
  gray: '#808080', grey: '#808080', maroon: '#800000', olive: '#808000',
  purple: '#800080', teal: '#008080', navy: '#000080', orange: '#ffa500',
  gold: '#ffd700', pink: '#ffc0cb', brown: '#a52a2a', beige: '#f5f5dc',
  indigo: '#4b0082', violet: '#ee82ee', tan: '#d2b48c', salmon: '#fa8072',
  khaki: '#f0e68c', crimson: '#dc143c', tomato: '#ff6347', coral: '#ff7f50',
  chocolate: '#d2691e', turquoise: '#40e0d0', lavender: '#e6e6fa',
  ivory: '#fffff0', snow: '#fffafa', mintcream: '#f5fffa',
};

/**
 * Convert any of the common SVG color string forms to canonical "#rrggbb".
 * Returns null when the string can't be parsed — caller leaves those colors
 * unclustered and out of the palette (but still preserved in the SVG).
 *
 * Handles: #rgb, #rrggbb, #rrggbbaa, rgb(), rgba(), and the named colors
 * above.  Alpha is stripped (palette only deals with the opaque color; the
 * stop-opacity / fill-opacity attribute is preserved separately by the
 * render pipeline).
 */
export function colorToHex(input) {
  if (typeof input !== 'string') return null;
  const s = input.trim().toLowerCase();
  if (!s || s === 'none' || s === 'transparent' || s === 'currentcolor' || s === 'inherit') return null;

  if (NAMED_COLORS[s]) return NAMED_COLORS[s];

  // Hex forms
  let m = s.match(/^#([0-9a-f]{3,8})$/);
  if (m) {
    let h = m[1];
    if (h.length === 3) h = h.split('').map((c) => c + c).join('');
    if (h.length === 4) h = h.slice(0, 3).split('').map((c) => c + c).join('');   // drop alpha
    if (h.length === 8) h = h.slice(0, 6);                                          // drop alpha
    if (h.length !== 6) return null;
    return '#' + h;
  }

  // rgb() / rgba()
  m = s.match(/^rgba?\(([^)]+)\)$/);
  if (m) {
    const parts = m[1].split(',').map((x) => x.trim());
    if (parts.length < 3) return null;
    const r = clamp255(parts[0]);
    const g = clamp255(parts[1]);
    const b = clamp255(parts[2]);
    if (r == null || g == null || b == null) return null;
    return '#' + [r, g, b].map((v) => v.toString(16).padStart(2, '0')).join('');
  }

  return null;
}

function clamp255(s) {
  if (s.endsWith('%')) {
    const f = parseFloat(s);
    if (!Number.isFinite(f)) return null;
    return Math.round(Math.max(0, Math.min(100, f)) * 2.55);
  }
  const f = parseFloat(s);
  if (!Number.isFinite(f)) return null;
  return Math.round(Math.max(0, Math.min(255, f)));
}

/** "#rrggbb" → { h:0-360, s:0-1, l:0-1 } */
export function hexToHsl(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s, hue;
  if (max === min) {
    hue = 0; s = 0;
  } else {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: hue = ((g - b) / d + (g < b ? 6 : 0)) * 60; break;
      case g: hue = ((b - r) / d + 2) * 60; break;
      default: hue = ((r - g) / d + 4) * 60;
    }
  }
  return { h: hue, s, l };
}

/** { h, s, l } → "#rrggbb" with hue wrapped, s/l clamped. */
export function hslToHex({ h, s, l }) {
  const H = ((h % 360) + 360) % 360 / 360;
  const S = Math.max(0, Math.min(1, s));
  const L = Math.max(0, Math.min(1, l));
  let r, g, b;
  if (S === 0) {
    r = g = b = L;
  } else {
    const q = L < 0.5 ? L * (1 + S) : L + S - L * S;
    const p = 2 * L - q;
    r = hue2rgb(p, q, H + 1 / 3);
    g = hue2rgb(p, q, H);
    b = hue2rgb(p, q, H - 1 / 3);
  }
  return '#' + [r, g, b].map((v) => Math.round(v * 255).toString(16).padStart(2, '0')).join('');
}

function hue2rgb(p, q, t) {
  if (t < 0) t += 1;
  if (t > 1) t -= 1;
  if (t < 1 / 6) return p + (q - p) * 6 * t;
  if (t < 1 / 2) return q;
  if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6;
  return p;
}

// ── Clustering ──────────────────────────────────────────────────────────────

const NEUTRAL_SAT_THRESHOLD = 0.10;   // s ≤ this → grayscale family
const HUE_FAMILY_DISTANCE   = 25;     // degrees — colors within this hue spread cluster

function hueDistance(a, b) {
  const d = Math.abs(a - b) % 360;
  return d > 180 ? 360 - d : d;
}

/**
 * Cluster a list of color strings into perceptual families.
 *
 * Returns Family[] where each family has:
 *   { id, kind: 'chromatic'|'neutral', members: [{ original, hex, hsl }],
 *     representative: '#rrggbb', avgHue, avgLightness, chromaScore }
 *
 * `original` is the EXACT source string from the SVG (e.g. "rgb(255,0,0)" or
 * "Red") — preserved so caller can write back into the colorMap by original
 * key, exactly the way applyColorMap expects.
 */
export function clusterPaletteFamilies(colorStrings) {
  // Parse + drop unparseable colors
  const parsed = [];
  const seenHex = new Set();
  for (const orig of colorStrings || []) {
    const hex = colorToHex(orig);
    if (!hex) continue;
    // Multiple SVG strings can parse to the same hex (e.g. "Red" and "#ff0000").
    // Keep all originals (they may map to different SVG nodes) but only one
    // member per unique-hex within the family — the representative math uses
    // hex, not original.
    parsed.push({ original: orig, hex, hsl: hexToHsl(hex) });
    seenHex.add(hex);
  }

  // Leader clustering — each family keeps an `anchorHue` set when the family
  // is first created.  Subsequent colors join the family if they fall within
  // HUE_FAMILY_DISTANCE of that anchor.  This is order-stable and avoids the
  // chicken-and-egg of needing avgHue before it's computed.
  const families = [];
  for (const p of parsed) {
    const isNeutral = p.hsl.s <= NEUTRAL_SAT_THRESHOLD;
    let family;
    if (isNeutral) {
      // Neutrals cluster purely by lightness band (light / mid / dark) so a
      // near-white and a near-black don't get lumped into one "grayscale".
      const band = p.hsl.l > 0.75 ? 'light' : p.hsl.l < 0.25 ? 'dark' : 'mid';
      family = families.find((f) => f.kind === 'neutral' && f.band === band);
      if (!family) {
        family = { id: `neutral-${band}`, kind: 'neutral', band, anchorHue: p.hsl.h, members: [] };
        families.push(family);
      }
    } else {
      family = families.find(
        (f) => f.kind === 'chromatic' && hueDistance(f.anchorHue, p.hsl.h) <= HUE_FAMILY_DISTANCE,
      );
      if (!family) {
        family = { id: `chromatic-${families.length}`, kind: 'chromatic', anchorHue: p.hsl.h, members: [] };
        families.push(family);
      }
    }
    family.members.push(p);
  }

  // Compute representative + summary stats per family
  for (const f of families) {
    // Representative: highest-chroma member, tie-break by closest to mid-L
    const scored = f.members.map((m) => ({
      m,
      score: m.hsl.s * (1 - Math.abs(0.5 - m.hsl.l) * 0.5),
    }));
    scored.sort((a, b) => b.score - a.score);
    f.representative = scored[0].m.hex;
    f.representativeHsl = scored[0].m.hsl;
    f.avgHue        = average(f.members.map((m) => m.hsl.h));
    f.avgLightness  = average(f.members.map((m) => m.hsl.l));
    f.maxChroma     = Math.max(...f.members.map((m) => m.hsl.s));
    // Score families for slot assignment: chromatic outranks neutral; within
    // chromatic, more members AND higher chroma win.
    const distinct = new Set(f.members.map((m) => m.hex)).size;
    f.chromaScore  = (f.kind === 'chromatic' ? 10 : 1) * distinct * (0.5 + f.maxChroma);
  }

  return families;
}

function average(arr) {
  if (!arr.length) return 0;
  return arr.reduce((a, b) => a + b, 0) / arr.length;
}

// ── Slot assignment ─────────────────────────────────────────────────────────

/**
 * Assign Primary / Secondary / Accent slots from a list of families.
 *
 * Heuristic (matches the user's example "#C11112 / #FFFFFF / #272727"):
 *   Primary   = the most prominent CHROMATIC family
 *   Secondary = the lightest NEUTRAL family if one exists, else the next
 *               chromatic family
 *   Accent    = the darkest NEUTRAL family if one exists, else the next
 *               chromatic family
 *
 * Falls back gracefully when fewer than 3 families exist — unused slots are
 * returned as null.
 */
export function pickPaletteSlots(families) {
  const chromatic = families.filter((f) => f.kind === 'chromatic').sort((a, b) => b.chromaScore - a.chromaScore);
  const neutrals  = families.filter((f) => f.kind === 'neutral').sort((a, b) => b.avgLightness - a.avgLightness);

  const taken = new Set();
  const take  = (f) => { if (f) taken.add(f.id); return f; };

  const primary   = take(chromatic[0]) || null;
  const lightNeutral = neutrals.find((n) => !taken.has(n.id) && n.avgLightness >= 0.5);
  const darkNeutral  = [...neutrals].reverse().find((n) => !taken.has(n.id) && n.avgLightness < 0.5);

  let secondary = lightNeutral || null;
  let accent    = darkNeutral || null;

  // Fill missing slots with leftover chromatic families
  const leftover = chromatic.filter((f) => !taken.has(f.id));
  if (!secondary) secondary = take(leftover.shift()) || null;
  else taken.add(secondary.id);
  if (!accent)    accent    = take(leftover.shift()) || null;
  else taken.add(accent.id);

  return { primary, secondary, accent };
}

// ── Family-aware remapping ──────────────────────────────────────────────────

/**
 * Piecewise-linear lightness remap.  Anchors the family's representative
 * lightness from oldRepL → newRepL and scales the two segments [0,oldRepL]
 * and [oldRepL,1] proportionally onto [0,newRepL] and [newRepL,1].
 *
 * Result: the darkest family member stays *the darkest* (without ever
 * collapsing to 0) and the lightest stays *the lightest* (without
 * collapsing to 1) — the whole tonal range stretches/squeezes together
 * around the new anchor.  Plain additive ΔL clamps at the extremes and
 * destroys the family's shading.
 */
function remapLightness(memberL, oldRepL, newRepL) {
  if (memberL <= oldRepL) {
    if (oldRepL < 1e-6) return newRepL;
    return (memberL / oldRepL) * newRepL;
  }
  if (oldRepL > 1 - 1e-6) return newRepL;
  return newRepL + ((memberL - oldRepL) / (1 - oldRepL)) * (1 - newRepL);
}

/**
 * For a single family member, compute its new color when the family's
 * representative shifts from oldRepHsl to newRepHsl.
 *
 *   newH = (member.h + ΔH) wrapped                     — preserves hue offset
 *   newS = member.s scaled to new rep's saturation     — keeps proportional
 *                                                         saturation; near-
 *                                                         grays stay near-gray
 *   newL = piecewise remap (member.l, old.l → new.l)   — preserves the whole
 *                                                         tonal range without
 *                                                         clamping at extremes
 */
export function remapMember(memberHsl, oldRepHsl, newRepHsl) {
  const dH = wrapDeg(newRepHsl.h - oldRepHsl.h);
  const kS = newRepHsl.s / Math.max(oldRepHsl.s, 0.0001);

  // For neutrals (old rep ~grayscale), saturation can't be "scaled" — fall
  // back to the new rep's absolute saturation when shifting away from gray.
  const useScale = oldRepHsl.s > 0.05;

  return {
    h: ((memberHsl.h + dH) % 360 + 360) % 360,
    s: useScale ? Math.max(0, Math.min(1, memberHsl.s * kS))
                : Math.max(0, Math.min(1, newRepHsl.s)),
    l: Math.max(0, Math.min(1, remapLightness(memberHsl.l, oldRepHsl.l, newRepHsl.l))),
  };
}

function wrapDeg(d) {
  let x = d % 360;
  if (x > 180)  x -= 360;
  if (x < -180) x += 360;
  return x;
}

/**
 * Build the colorMap diff for one palette slot change.  Returns a partial
 * `{ original → newColor }` map covering every member of the family with the
 * new target color applied.  Caller merges this into the element's full
 * colorMap.
 *
 * `newTargetColor` is whatever the user picked in the swatch (hex string).
 * The remap is computed against the family's ORIGINAL representative — never
 * the previous target — so palette edits are idempotent in target color.
 */
export function buildFamilyRemap(family, newTargetColor) {
  if (!family || !family.members.length) return {};
  const newHex = colorToHex(newTargetColor);
  if (!newHex) return {};
  const newHsl = hexToHsl(newHex);
  const oldHsl = family.representativeHsl;

  const out = {};
  for (const m of family.members) {
    const remapped = remapMember(m.hsl, oldHsl, newHsl);
    out[m.original] = hslToHex(remapped);
  }
  return out;
}

// ── Top-level: build a palette for an SVG ───────────────────────────────────

/**
 * Build a palette descriptor for a raw SVG string.  Pulls the unique color
 * list from svgUtils.extractSvgColors (so we automatically benefit from
 * <style> + inline style + attribute scanning), clusters into families,
 * and assigns Primary/Secondary/Accent slots.
 *
 * Returns:
 *   {
 *     colors:    string[]      // raw color list from the SVG
 *     families:  Family[]
 *     slots:     { primary, secondary, accent }   // family refs or null
 *     defaults:  { primary, secondary, accent }   // hex defaults to show
 *                                                  in the swatches
 *   }
 */
export function buildSvgPalette(svgContent) {
  const colors   = extractSvgColors(svgContent || '');
  const families = clusterPaletteFamilies(colors);
  const slots    = pickPaletteSlots(families);
  const defaults = {
    primary:   slots.primary   ? slots.primary.representative   : null,
    secondary: slots.secondary ? slots.secondary.representative : null,
    accent:    slots.accent    ? slots.accent.representative    : null,
  };
  return { colors, families, slots, defaults };
}

/**
 * Given an element's current `palette` field and the family for a slot,
 * tell us whether the palette has been edited away from the parsed default.
 */
export function isPaletteSlotEdited(palette, slot, family) {
  if (!palette || !family) return false;
  const current = palette[slot];
  if (current == null) return false;
  const curHex = colorToHex(current);
  return curHex && curHex.toLowerCase() !== family.representative.toLowerCase();
}
